import React from "react";
import { Navigate, useParams } from "react-router-dom";

import { isReservedCompanyId } from "@/pages/companies/types";
import { isReservedPersonId } from "@/entities/people/types";

type GuardedEntity = "person" | "company";

interface ReservedIdRouteGuardProps {
  entity: GuardedEntity;
  children: React.ReactNode;
}

const entityConfig: Record<
  GuardedEntity,
  { param: string; listPath: string; isReserved: (id?: string) => boolean }
> = {
  person: {
    param: "personId",
    listPath: "/people",
    isReserved: isReservedPersonId
  },
  company: {
    param: "companyId",
    listPath: "/companies",
    isReserved: isReservedCompanyId
  }
};

// Redirects away from detail/log routes when the URL id is a reserved sentinel
// (e.g. p-unknown, p-none, p-other, c-other). Sentinels are FK integrity
// placeholders and must never be rendered in an editable view — opening one
// lets autosave corrupt the sentinel for the whole tenant (CRM-6081).
const ReservedIdRouteGuard: React.FC<ReservedIdRouteGuardProps> = ({
  entity,
  children
}) => {
  const { param, listPath, isReserved } = entityConfig[entity];
  const params = useParams();
  const id = params[param];

  if (isReserved(id)) {
    return <Navigate to={listPath} replace />;
  }

  return <>{children}</>;
};

export default ReservedIdRouteGuard;
